import { PageContainer } from "components/core/basic/PageContainer";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "dates/DatesContext";
import { useDeferredValue, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { createColumnBuilder } from "components/table/columnBuilder";
import { idsToVisibilityState, selectionStateToSelectedIds } from "components/table/utils";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { useQuery } from "@tanstack/react-query";
import { buildMetric } from "views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { type OrgQueryV4Row } from "../perf_reports/api/organization/v4OrgReportByOrg";
import { useGetV4OrgReportByDate } from "../perf_reports/api/organization/v4OrgReportByDate";
import { useGetV4OrgReportTotals } from "../perf_reports/api/organization/v4OrgReportTotals";
import AgencyDashboardTable from "./AgencyDashboardTable";
import MarketplaceStatsFilter from "views/merchandise/MarketplaceStatsFilter";
import Accordion from "components/core/basic/Accordion";
import { marketplaceDict } from "components/custom/analytics/ShelfForm";
import { getOrganizationsOptions } from "modules/admin/api/useOrganizations";
import { OrgCell } from "./OrgCell";

const columnHelper = createColumnBuilder<OrgQueryV4Row>();

export const AGENCY_DASHBOARD_STAT_CHANNELS = ["AMAZON", "WALMART"];

const OrgReportBody = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const [selectedMarketplace, setSelectedMarketplace] = useState(marketPlace);
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false);

  const trellisProfilesQuery = useQuery(getOrganizationsOptions());

  const acosOrRoas = useAcosOrRoas();
  const { start, end, group } = useDates();

  const marketPlaceOptions = useMemo(() => {
    return [...new Set(trellisProfilesQuery.data?.map((profile) => profile.marketplaces).flat())];
  }, [trellisProfilesQuery.data]);

  const standardMetricIds = useStandardEntityMetrics("organization");

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: "Organization",
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: (props) => {
          return <OrgCell orgData={props.row.original} marketPlace={selectedMarketplace} />;
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId, { formatOptions: { overrideMarketplace: selectedMarketplace } });
      }),
    ],
    [standardMetricIds, selectedMarketplace],
  );
  const defaultSelectedColumns = [
    "name",
    "sales",
    "units",
    acosOrRoas("acos", "roas"),
    "margin",
    "ad_sales",
    "ad_spend",
    acosOrRoas("roas", "acos"),
    "roms",
    "cost",
    "promo_cost",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      tags: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["name"],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `agencyDashboardOrgReport`,
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const filters = useMemo(() => {
    const hiddenOrgsFilter =
      showHiddenOrgs ? [] : [{ id: "org_is_visible", value: { op: "=", value: !showHiddenOrgs } }];
    const selectedIdsFilter = selectedIds.length ? [{ id: "org_id", value: { op: "anyof", value: selectedIds } }] : [];
    const orgChannelFilter = [
      {
        id: "org_type",
        value: {
          op: "anyof",
          value: AGENCY_DASHBOARD_STAT_CHANNELS,
        },
      },
    ];

    return [...orgChannelFilter, ...state.columnFilters, ...hiddenOrgsFilter, ...selectedIdsFilter];
  }, [state.columnFilters, showHiddenOrgs, selectedIds]);

  const byDateOptionsGetter = useGetV4OrgReportByDate(selectedMarketplace);
  const orgsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );
  const totalOptionsGetter = useGetV4OrgReportTotals(selectedMarketplace);
  const orgTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total: orgTotalsReportQuery.data?.data,
    };
    return standardMetricIds.map((metricId) =>
      buildMetric({ marketPlace: marketPlace }, data, metricId, COMPARE_METRIC_LOOKUP[metricId], {
        overrideMarketplace: selectedMarketplace,
      }),
    );
  }, [orgTotalsReportQuery.data?.data, standardMetricIds, marketPlace, selectedMarketplace]);

  return (
    <PageContainer withBreadcrumb={true}>
      <MetricsCard
        id={"agencyDashboardOrgReport"}
        title={"Performance Overview"}
        data={{ date: orgsByDateReportQuery.data?.data ?? [], total: orgTotalsReportQuery.data?.data }}
        loading={
          orgsByDateReportQuery.isFetching && (!orgsByDateReportQuery.data || orgsByDateReportQuery.isPlaceholderData)
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          orgTotalsReportQuery.isFetching && (!orgTotalsReportQuery.data || orgTotalsReportQuery.isPlaceholderData)
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["sales", "ad_sales", "ad_spend"]}
        group={group}
        // Hiding annotations since they only get fetch for current profile
        showAnnotations={false}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"target"}
        inlineStatus={
          <MarketplaceStatsFilter
            selectedMarketplace={selectedMarketplace}
            setSelectedMarketplace={setSelectedMarketplace}
            statMarketplaceOptions={marketPlaceOptions}
          />
        }
      >
        <Accordion
          title={`Brands in ${marketplaceDict[selectedMarketplace as keyof typeof marketplaceDict]}`}
          initialExpanded={true}
        >
          <AgencyDashboardTable
            state={state}
            initialState={initialState}
            setters={setters}
            key={"organization"}
            selectedMarketplace={selectedMarketplace}
            showHiddenOrgs={showHiddenOrgs}
            setShowHiddenOrgs={setShowHiddenOrgs}
            columns={columns}
          />
        </Accordion>
      </MetricsCard>
    </PageContainer>
  );
};

const AgencyOrgReport = () => {
  const breadCrumbItems = [
    {
      name: "Agency Dashboard",
      href: "/user/agency/dashboard",
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <OrgReportBody />
    </DatesProvider>
  );
};

export default AgencyOrgReport;
